import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type { PriceListUpsertInput } from '../domain/priceListTypes';

function buildItemsPayload(items: PriceListUpsertInput['items']) {
  // Server expects camelCase keys (v_item->>'productId', etc.)
  return (items ?? []).map((item) => ({
    id: item.id,
    productId: item.productId,
    variantId: item.variantId ?? null,
    price: item.price ?? null,
    percentOff: item.percentOff ?? null,
    minQty: item.minQty ?? null,
  }));
}

export async function applyPriceListRemote(
  operation: 'insert' | 'update' | 'delete',
  args: {
    id: string;
    branchId: string;
    operationId: string;
    input?: PriceListUpsertInput;
    updatedAt?: string;
    createdAt?: string;
  },
): Promise<{ ok: boolean; id?: string; code?: string }> {
  const payload: Record<string, unknown> = {
    id: args.id,
    branchId: args.branchId,
    operationId: args.operationId,
    updatedAt: args.updatedAt ?? new Date().toISOString(),
  };

  if (operation !== 'delete' && args.input) {
    payload.name = args.input.name;
    payload.code = args.input.code ?? null;
    payload.isDefault = args.input.isDefault ?? false;
    payload.appliesOnline = args.input.appliesOnline ?? false;
    payload.isActive = args.input.isActive ?? true;
    payload.pricingMode = args.input.pricingMode ?? 'fixed';
    payload.createdAt = args.createdAt ?? args.updatedAt ?? new Date().toISOString();
    payload.items = buildItemsPayload(args.input.items);
  }

  const { data, error } = await supabase.schema('pos').rpc('apply_price_list_command', {
    p_operation: operation,
    p_payload: payload,
  });

  if (error) {
    captureOpsFailure({
      module: 'price-lists',
      op: 'price_lists.write',
      error,
      ingestError: error.message,
      applied: false,
      itemId: args.id,
      extra: { priceListId: args.id, branchId: args.branchId, operation },
    });
    throw error;
  }
  const result = (data ?? { ok: false }) as { ok: boolean; id?: string; code?: string };
  if (!result.ok) {
    const fail = new Error(result.code ?? 'price_list_save_failed');
    captureOpsFailure({
      module: 'price-lists',
      op: 'price_lists.write',
      error: fail,
      ingestError: result.code ?? 'price_list_save_failed',
      applied: false,
      itemId: args.id,
      extra: { priceListId: args.id, branchId: args.branchId, operation },
    });
    throw fail;
  }
  return result;
}

export async function applyCustomerPriceListRemote(args: {
  customerId: string;
  branchId: string;
  operationId: string;
  priceListId?: string | null;
}): Promise<void> {
  const { error } = await supabase.schema('customers').rpc('apply_customer_price_list_command', {
    p_payload: {
      customerId: args.customerId,
      branchId: args.branchId,
      operationId: args.operationId,
      priceListId: args.priceListId ?? null,
      updatedAt: new Date().toISOString(),
    },
  });
  if (error) {
    captureOpsFailure({
      module: 'price-lists',
      op: 'price_lists.assign_customer',
      error,
      ingestError: error.message,
      itemId: args.customerId,
      extra: { customerId: args.customerId, branchId: args.branchId },
    });
    throw error;
  }
}
